import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { coral, green, green25, grey25, navy, pink10, white } from '@zocdoc/branding/colors';

import {
    INPUT_HEIGHT,
    NON_INPUT_X_AFFORDANCE_DESKTOP,
    NON_INPUT_X_AFFORDANCE_MOBILE,
} from '../../../../picker-constants';

import { FontFamily } from '../../../../typography';
import { breakpoint } from '../../../../grid';
import { Spinner } from '../../../../Spinner';
import { InvertedCheck, Alert } from '../../../../icons';

const AcceptedIcon = props => (
    <StyledInvertedCheck
        {...props}
        color={green}
        data-test="insurance-accepted-icon"
    />
);

const NotAcceptedIcon = props => (
    <StyledAlert
        {...props}
        color={coral}
        data-test="insurance-not-accepted-icon"
    />
);

const LoadingIcon = props => (
    <Spinner
        {...props}
        alt="Loading"
        data-test="insurance-picker-spinner"
        inline
        size={24}
    />
);

class BaseSelectedStateView extends Component {
    render() {
        const SelectedItem = this.getSelectedItem();
        return (
            <SelectedState
                data-test="selected-state-section"
                onClick={this.props.openDropdown}
            >
                <SelectedItem>
                    {this.maybeRenderIcon()}
                    <SelectedStateName
                        data-prod-guids="selected-state-name:[003703d2]"
                        data-test="selected-state-name"
                    >
                        {this.getName()}
                    </SelectedStateName>
                </SelectedItem>
            </SelectedState>
        );
    }

    maybeRenderIcon() {
        if (this.props.hideValidation) {
            return null;
        }

        const Icon = this.getIcon();

        if (Icon) {
            return (<IconWrapper data-test="insurance-validation-icon"><Icon /></IconWrapper>);
        }
    }

    getIcon() {
        const { insuranceIsAccepted, insuranceAcceptanceLoading } = this.props;

        if (insuranceAcceptanceLoading) {
            return LoadingIcon;
        } else if (insuranceIsAccepted) {
            return AcceptedIcon;
        } else if (insuranceIsAccepted !== undefined) {
            return NotAcceptedIcon;
        }
    }

    getName() {
        const {
            carrierName,
            planName,
        } = this.props;

        let name = carrierName;

        if (planName) {
            name += ` - ${planName}`;
        }

        return name;
    }

    getSelectedItem() {
        const { hideValidation, showValidationBackground, insuranceIsAccepted } = this.props;
        if (hideValidation || !showValidationBackground) {
            // No border
            return BaseSelectedItem;
        } else if (insuranceIsAccepted) {
            // Green valid state
            return ValidSelectedItem;
        } else if (insuranceIsAccepted !== undefined) {
            // Red invalid state
            return InvalidSelectedItem;
        } else {
            // Gray default border
            return DefaultSelectedItem;
        }
    }
}

const SelectedState = styled.ol`
    background-color: ${white};
    min-height: ${INPUT_HEIGHT}px;
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
`;

const lineHeight = 24;

const BaseSelectedItem = styled.li`
    font: 15px/${lineHeight}px ${FontFamily.SharpSansMedium};
    color: ${navy};
    padding: ${(INPUT_HEIGHT - lineHeight) / 2}px ${NON_INPUT_X_AFFORDANCE_MOBILE}px;
    cursor: pointer;
    display: flex;

    ${breakpoint.small} {
        padding: ${(INPUT_HEIGHT - lineHeight) / 2}px ${NON_INPUT_X_AFFORDANCE_DESKTOP}px;
        white-space: nowrap;
        overflow-x: hidden;
        cursor: pointer;
        text-overflow: ellipsis;

        &:hover {
            .selected-state-name {
                white-space: normal;
                text-overflow: inherit;
                display: inline-flex;
            }
        }
    }
`;

const DefaultSelectedItem = BaseSelectedItem.extend`
    border: 1px solid ${grey25};
    background-color: ${white};
`;

const ValidSelectedItem = BaseSelectedItem.extend`
    border: 1px solid ${green};
    background-color: ${green25};
`;

const InvalidSelectedItem = BaseSelectedItem.extend`
    border: 1px solid ${coral};
    background-color: ${pink10};
`;

const SelectedStateName = styled.p`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow-x: hidden;

    &:hover {
        white-space: normal;
        text-overflow: inherit;
        display: inline-flex;
    }
`;

const IconWrapper = styled.div`
    padding-right: 12px;
    display: inline-flex;
`;

const StyledInvertedCheck = styled(InvertedCheck)`
    font-size: 15px !important;
    margin-top: 4px;
`;

const StyledAlert = styled(Alert)`
    font-size: 15px !important;
    margin-top: 4px;
`;

export const SelectedStateView = injectIntl(BaseSelectedStateView);

export const SelectedStateViewPrimitives = {
    SelectedState,
};
